/**
 * Secure Cookie Utility for Moria Frontend Application
 * 
 * This utility provides secure cookie management with proper security flags:
 * - HttpOnly: Prevents XSS attacks by making cookies inaccessible to JavaScript (for sensitive data)
 * - Secure: Ensures cookies are only sent over HTTPS in production
 * - SameSite: Prevents CSRF attacks
 * - Path: Restricts cookie scope to the application
 * - Domain: Controls which domains can access the cookie
 */

import AUTH_CONFIG from '../config/authConfig';

class CookieManager {
  constructor() {
    this.isProduction = process.env.NODE_ENV === 'production';
    this.defaultOptions = {
      secure: this.isProduction, // Only use secure flag in production (HTTPS)
      sameSite: 'Strict', // Strict SameSite policy for security
      path: '/', // Available across the entire application
    };
  }

  /**
   * Set a cookie with security flags
   * @param {string} name - Cookie name
   * @param {string} value - Cookie value
   * @param {Object} options - Additional cookie options
   * @param {number} options.maxAge - Max age in seconds
   * @param {Date} options.expires - Expiration date
   * @param {boolean} options.httpOnly - Whether cookie should be HttpOnly (not available for sensitive data in client-side)
   * @param {boolean} options.secure - Whether cookie should only be sent over HTTPS
   * @param {string} options.sameSite - SameSite policy ('Strict', 'Lax', 'None')
   * @param {string} options.path - Cookie path
   * @param {string} options.domain - Cookie domain
   */
  setCookie(name, value, options = {}) {
    const cookieOptions = { ...this.defaultOptions, ...options };
    
    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
    
    if (cookieOptions.maxAge !== undefined) {
      cookieString += `; Max-Age=${cookieOptions.maxAge}`;
    }
    
    if (cookieOptions.expires) {
      cookieString += `; Expires=${cookieOptions.expires.toUTCString()}`;
    }
    
    if (cookieOptions.path) {
      cookieString += `; Path=${cookieOptions.path}`;
    }
    
    if (cookieOptions.domain) {
      cookieString += `; Domain=${cookieOptions.domain}`;
    }
    
    if (cookieOptions.secure) {
      cookieString += '; Secure';
    }
    
    if (cookieOptions.sameSite) {
      cookieString += `; SameSite=${cookieOptions.sameSite}`;
    }
    
    // Note: HttpOnly cannot be set from client-side JavaScript
    // For sensitive data like JWT tokens, consider using server-side cookies
    
    document.cookie = cookieString;
  }

  /**
   * Get a cookie value by name
   * @param {string} name - Cookie name
   * @returns {string|null} Cookie value or null if not found
   */
  getCookie(name) {
    const nameEQ = encodeURIComponent(name) + '=';
    const cookies = document.cookie.split(';');
    
    for (let cookie of cookies) {
      let c = cookie.trim();
      if (c.indexOf(nameEQ) === 0) {
        return decodeURIComponent(c.substring(nameEQ.length));
      }
    }
    return null;
  }

  /**
   * Remove a cookie by setting its expiration to the past
   * @param {string} name - Cookie name
   * @param {Object} options - Additional options (path, domain)
   */
  removeCookie(name, options = {}) {
    const cookieOptions = { 
      ...this.defaultOptions, 
      ...options, 
      expires: new Date(0),
      maxAge: -1
    };
    this.setCookie(name, '', cookieOptions);
  }

  /**
   * Set a session cookie (expires when browser closes)
   * @param {string} name - Cookie name
   * @param {string} value - Cookie value
   * @param {Object} options - Additional options
   */
  setSessionCookie(name, value, options = {}) {
    // Session cookies don't have maxAge or expires
    const { maxAge, expires, ...sessionOptions } = options;
    this.setCookie(name, value, sessionOptions);
  }

  /**
   * Set a persistent cookie with expiration
   * @param {string} name - Cookie name
   * @param {string} value - Cookie value
   * @param {number} daysToExpire - Days until expiration
   * @param {Object} options - Additional options
   */
  setPersistentCookie(name, value, daysToExpire = 30, options = {}) {
    const expires = new Date();
    expires.setDate(expires.getDate() + daysToExpire);
    this.setCookie(name, value, { ...options, expires });
  }

  /**
   * Set a secure token cookie (for JWT tokens)
   * @param {string} name - Cookie name
   * @param {string} token - Token value
   * @param {number} daysToExpire - Days until expiration
   */
  setTokenCookie(name, token, daysToExpire = 7) {
    this.setPersistentCookie(name, token, daysToExpire, {
      secure: this.isProduction,
      sameSite: 'Strict',
      // Note: In a production environment, consider setting these cookies
      // from the server-side with HttpOnly flag for maximum security
    });
  }

  /**
   * Set a cart-related cookie with appropriate security
   * @param {string} name - Cookie name
   * @param {string} value - Cookie value
   * @param {number} daysToExpire - Days until expiration
   */
  setCartCookie(name, value, daysToExpire = 30) {
    this.setPersistentCookie(name, value, daysToExpire, {
      secure: this.isProduction,
      sameSite: 'Lax', // Lax for cart functionality across navigation
    });
  }

  /**
   * Set a user preference cookie
   * @param {string} name - Cookie name
   * @param {string} value - Cookie value
   * @param {number} daysToExpire - Days until expiration
   */
  setPreferenceCookie(name, value, daysToExpire = 365) {
    this.setPersistentCookie(name, value, daysToExpire, {
      secure: this.isProduction,
      sameSite: 'Lax',
    });
  }

  /**
   * Check if cookies are enabled in the browser
   * @returns {boolean} True if cookies are enabled
   */
  areCookiesEnabled() {
    try {
      const testName = 'test_cookie_enabled';
      this.setCookie(testName, 'test');
      const isEnabled = this.getCookie(testName) === 'test';
      this.removeCookie(testName);
      return isEnabled;
    } catch (e) {
      return false;
    }
  }

  /**
   * Get all cookies as an object
   * @returns {Object} Object with cookie names as keys and values as values
   */
  getAllCookies() {
    const cookies = {};
    if (document.cookie) {
      document.cookie.split(';').forEach(cookie => {
        const [name, value] = cookie.trim().split('=').map(decodeURIComponent);
        if (name) {
          cookies[name] = value || '';
        }
      });
    }
    return cookies;
  }

  /**
   * Clear all application cookies
   * @param {string[]} cookieNames - Array of cookie names to clear
   */
  clearAllCookies(cookieNames = []) {
    if (cookieNames.length === 0) {
      // Get all cookies and remove them
      const allCookies = this.getAllCookies();
      cookieNames = Object.keys(allCookies);
    }
    
    cookieNames.forEach(name => {
      this.removeCookie(name);
      // Also try removing with different path options
      this.removeCookie(name, { path: '/' });
      this.removeCookie(name, { path: '', domain: window.location.hostname });
    });
  }
}

/**
 * Utility function to check if user is logged in based on login status cookie
 * This is used to prevent unnecessary refresh token calls for guest users
 * @returns {boolean} True if user appears to be logged in
 */
export function isUserLoggedIn() {
  try {
    // Check for the backend-set login status cookie (mls)
    const loginStatusCookieName = AUTH_CONFIG.COOKIE_NAMES.LOGIN_STATUS; // 'mls'
    const loginStatus = document.cookie
      .split('; ')
      .find(row => row.startsWith(`${loginStatusCookieName}=`))
      ?.split('=')[1];
    
    return loginStatus === 'true';
  } catch (error) {
    console.warn('Error checking login status from cookie:', error);
    return false;
  }
}

/**
 * Utility function to determine if refresh token should be attempted
 * Based on the login status cookie set by the backend
 * @returns {boolean} True if refresh token attempt should be made
 */
export function shouldAttemptRefresh() {
  const isLoggedIn = isUserLoggedIn();
  // console.log('Login status from cookie:', isLoggedIn);
  return isLoggedIn;
}

// Create a singleton instance
const cookieManager = new CookieManager();

export default cookieManager;

// Export specific methods for convenience
export const {
  setCookie,
  getCookie,
  removeCookie,
  setSessionCookie,
  setPersistentCookie,
  setTokenCookie,
  setCartCookie,
  setPreferenceCookie,
  areCookiesEnabled,
  getAllCookies,
  clearAllCookies
} = cookieManager;
